import { useState, useCallback } from 'react';
import { useAIUsageCacheInvalidation } from './useAIUsageCacheInvalidation';
import { useGlobalModals } from '@/contexts/GlobalModalsContext';
import { useAIUsageData } from '@/hooks/subscription/useAIUsageData';

interface AIOperationOptions {
  /** Whether user expects immediate visual feedback (default: true) */
  immediate?: boolean;
  /** Custom error message prefix */
  errorPrefix?: string;
  /** Whether to log the operation for debugging */
  debug?: boolean;
}

interface AIOperationResult<T> {
  data: T | null;
  error: string | null;
  isLoading: boolean;
}

/**
 * Universal wrapper for all AI operations
 * Automatically handles:
 * - Pre-operation credit checking (blocks if no credits)
 * - Cache invalidation (immediate or deferred)
 * - Loading states
 * - Error handling
 * - Debug logging
 * 
 * Use this for any operation that consumes AI credits
 */
export const useAIOperationWrapper = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { invalidateAIUsageData } = useAIUsageCacheInvalidation();
  const { openOutOfCreditsModal } = useGlobalModals();
  const usageData = useAIUsageData();

  /**
   * Wrap any AI operation with automatic credit checking and cache invalidation
   * @param operation - The AI operation function to execute
   * @param options - Configuration options
   */
  const wrapAIOperation = useCallback(async <T>(
    operation: () => Promise<T>,
    options: AIOperationOptions = {}
  ): Promise<AIOperationResult<T>> => {
    const {
      immediate = true,
      errorPrefix = 'AI operation failed',
      debug = false
    } = options;

    setIsLoading(true);
    setError(null);

    const startTime = Date.now();
    
    if (debug) {
      console.log('🤖 [AIOperationWrapper] Starting AI operation...');
    }

    try {
      // 🚫 BEFORE: Check if out of credits (BLOCKING)
      if (usageData.remainingBalance === 0) {
        if (debug) {
          console.log('🚫 [AIOperationWrapper] Blocking operation - no credits remaining');
        }
        openOutOfCreditsModal();
        throw new Error('Insufficient credits - operation blocked');
      }

      if (debug) {
        console.log(`🤖 [AIOperationWrapper] Credit check passed - ${usageData.remainingBalance} credits remaining`);
      }

      // ✅ Execute the AI operation
      const result = await operation();
      
      // 🔄 Automatically invalidate cache after successful operation
      await invalidateAIUsageData(immediate);
      
      const duration = Date.now() - startTime;
      
      if (debug) {
        console.log(`🤖 [AIOperationWrapper] Operation completed successfully in ${duration}ms`);
      }

      return {
        data: result,
        error: null,
        isLoading: false
      };

    } catch (err: any) {
      const errorMessage = err instanceof Error ? err.message : String(err);
      const fullError = `${errorPrefix}: ${errorMessage}`;
      
      setError(fullError);
      
      const duration = Date.now() - startTime;
      
      if (debug) {
        console.error(`🤖 [AIOperationWrapper] Operation failed after ${duration}ms:`, err);
      }

      return {
        data: null,
        error: fullError,
        isLoading: false
      };
    } finally {
      setIsLoading(false);
    }
  }, [invalidateAIUsageData, openOutOfCreditsModal, usageData]);

  /**
   * Execute an AI operation with automatic cache invalidation
   * Simpler interface for one-off operations
   */
  const executeAIOperation = useCallback(async <T>(
    operation: () => Promise<T>,
    options: AIOperationOptions = {}
  ): Promise<T> => {
    const result = await wrapAIOperation(operation, options);
    
    if (result.error) {
      throw new Error(result.error);
    }
    
    return result.data!;
  }, [wrapAIOperation]);

  return {
    wrapAIOperation,
    executeAIOperation,
    isLoading,
    error,
    clearError: () => setError(null)
  };
}; 